import { Bot, Crosshair, Route, Sparkles, Wrench } from "lucide-react";
import { useId } from "react";
import { Badge } from "../../components/Badge.js";

/**
 * Model-generated prose about a finding, rendered so a reader can never mistake
 * it for the record (Task 16).
 *
 * ## Why the marker is here and not in the payload
 *
 * A drafted fix is checked before anyone sees it. An explanation is not, and
 * cannot be — no second checker decides whether a paragraph of English is true.
 * So the safety property this file carries is the *only* one available: the
 * reader always knows which text is prose and which is record.
 *
 * That means the marker cannot be data. A field on the payload could arrive
 * absent, `false`, empty, or spelled differently, and every one of those is a
 * value a reply might produce. `MODEL_EXPLANATION_MARKER` is a constant in this
 * module, emitted before the prose on every render, from a component whose props
 * carry no way to turn it off — there is no `showMarker`, no `variant`, no
 * `hideLabel`. A reply cannot disable something the renderer never consults it
 * about.
 *
 * Three attacks, three answers, all three tested in `ModelExplanation.test.tsx`:
 *
 * 1. **A payload that claims the marker off** — `{ modelGenerated: false }`,
 *    `{ marker: "" }`. Nothing here reads those fields, so they do nothing.
 * 2. **A payload carrying markup** — `</section><style>…{display:none}</style>`.
 *    Paragraphs are React text nodes, so the markup is escaped and the panel
 *    cannot be closed early or styled away. `parseModelExplanation` also refuses
 *    markup outright, so this is belt and braces.
 * 3. **A payload impersonating the marker** — prose whose text *is*
 *    "Model-generated explanation". The marker is structural, not textual: it is
 *    an element carrying `data-model-generated="true"`, and exactly one such
 *    element exists per panel. Prose can copy the words; it cannot copy the
 *    attribute, because escaping keeps it a text node.
 *
 * ## Why this file validates the payload again
 *
 * `packages/model-assist/src/tasks/explain.ts` already validated the reply
 * before storing it. This module cannot import that check — the console is a
 * browser bundle and that package is server-side — so the contract is restated
 * here, deliberately, as a second boundary rather than as a shared one. A
 * payload arriving from the API is untrusted input to the console exactly as a
 * provider's reply is untrusted input to the task, and the two limits are kept
 * numerically identical so the pair cannot disagree about what an explanation is.
 *
 * A payload that fails renders **nothing**. Not the paragraphs that passed: a
 * partially-rendered explanation is a paragraph missing its qualification, and
 * it reads as a conclusion. All of it or none of it.
 */

/** The words a reader sees. A constant, so nothing that arrives can change it. */
export const MODEL_EXPLANATION_MARKER = "Model-generated explanation";

/**
 * The structural half of the marker. Exactly one element per panel carries it,
 * and prose cannot forge it: React escapes text, so a paragraph containing this
 * attribute renders as characters rather than as an attribute.
 */
export const MODEL_EXPLANATION_MARKER_ATTRIBUTE = "data-model-generated";

/** Identical to `explain.ts`'s limits, restated because it cannot import them. */
export const MAX_EXPLANATION_PARAGRAPHS = 3;
export const MAX_EXPLANATION_PARAGRAPH_LENGTH = 1_600;
export const MIN_EXPLANATION_PARAGRAPH_LENGTH = 24;

const MARKUP = /<[/!?a-zA-Z]/u;

export interface ModelExplanationView {
  readonly paragraphs: readonly string[];
  readonly quality: {
    readonly schemaVersion: "model-security-brief-quality/v1";
    readonly findingCitation: string;
    readonly evidenceCitation: string;
    readonly evidenceReplayStatus: "unverified" | "matched" | "mismatch" | "unavailable";
    readonly wordCounts: readonly [number, number, number];
    readonly requiredSections: readonly ["behavior", "attack_path", "engineering_response"];
    readonly advisoryOnly: true;
    readonly briefSha256: string;
  };
}

const DEFAULT_BRIEF_SECTION = Object.freeze({
  title: "Security interpretation",
  eyebrow: "What the finding means",
  Icon: Crosshair,
});

const BRIEF_SECTIONS = Object.freeze([
  DEFAULT_BRIEF_SECTION,
  {
    title: "Likely attack path",
    eyebrow: "How abuse could unfold",
    Icon: Route,
  },
  {
    title: "Engineering response",
    eyebrow: "What to change and verify",
    Icon: Wrench,
  },
]);

function finishPreviewFragment(text: string): string {
  let fragment = text.trimEnd();
  const lastConnector = fragment.search(/\s+(and|or)\s+[^,.;:!?]*$/iu);
  if (lastConnector > 40) fragment = fragment.slice(0, lastConnector);
  fragment = fragment.replace(
    /\s+(and|or|the|a|an|to|with|from|for|of|in|against|through|into)$/iu,
    "",
  );
  const finished = fragment.replace(/[,:;]\s*$/u, "");
  return `${finished.charAt(0).toUpperCase()}${finished.slice(1)}.`;
}

function briefSignal(paragraph: string): string {
  const normalized = paragraph.replace(/\s+/gu, " ").trim();
  const firstSentence = /.*?[.!?](?:\s|$)/u.exec(normalized)?.[0]?.trim() ?? normalized;
  if (firstSentence.length <= 118) return firstSentence;
  const clauses = firstSentence
    .split(/(?<=[,;:])\s+/u)
    .filter(Boolean)
    .filter((clause) => clause.replace(/[,:;]\s*$/u, "").trim().length >= 16);
  let clauseSummary = "";
  for (const clause of clauses) {
    const nextSummary = clauseSummary ? `${clauseSummary} ${clause}` : clause;
    if (nextSummary.length > 108) break;
    clauseSummary = nextSummary;
  }
  if (clauseSummary.length >= 40) {
    return finishPreviewFragment(clauseSummary);
  }
  const truncated = firstSentence
    .slice(0, 117)
    .replace(/\s+\S*$/u, "")
    .trimEnd();
  return finishPreviewFragment(truncated || firstSentence.slice(0, 117).trimEnd());
}

function compactCitation(citation: string): string {
  const inner = /^\[([a-z]+):([^\]]+)\]$/u.exec(citation);
  if (!inner) return citation;
  const [, type, id] = inner;
  if (!type || !id) return citation;
  return `[${type}:${id.length <= 16 ? id : id.slice(-12)}]`;
}

function hasControlCharacter(text: string): boolean {
  for (const character of text) {
    const code = character.codePointAt(0) ?? 0;
    if (code === 0x7f || (code < 0x20 && code !== 0x09)) return true;
  }
  return false;
}

/**
 * Validate an artifact payload into something renderable, or `null`.
 *
 * `unknown` in, because what arrives is a JSON payload from a table whose
 * contents originated outside the trust boundary. Every failure returns `null`
 * and the panel renders nothing; there is no path that returns a partial view.
 */
export function parseModelExplanation(value: unknown): ModelExplanationView | null {
  if (typeof value !== "object" || value === null) return null;
  const candidate = value as { paragraphs?: unknown; quality?: unknown };
  const paragraphs = candidate.paragraphs;
  if (!Array.isArray(paragraphs)) return null;
  if (paragraphs.length !== MAX_EXPLANATION_PARAGRAPHS) return null;
  for (const paragraph of paragraphs) {
    if (typeof paragraph !== "string") return null;
    if (paragraph.length < MIN_EXPLANATION_PARAGRAPH_LENGTH) return null;
    if (paragraph.length > MAX_EXPLANATION_PARAGRAPH_LENGTH) return null;
    if (hasControlCharacter(paragraph) || MARKUP.test(paragraph)) return null;
  }
  const quality = candidate.quality;
  if (typeof quality !== "object" || quality === null) return null;
  const receipt = quality as Record<string, unknown>;
  if (
    receipt.schemaVersion !== "model-security-brief-quality/v1" ||
    typeof receipt.findingCitation !== "string" ||
    !/^\[finding:[^\]]+\]$/u.test(receipt.findingCitation) ||
    typeof receipt.evidenceCitation !== "string" ||
    !/^\[evidence:[a-f0-9]{12}\]$/u.test(receipt.evidenceCitation) ||
    !["unverified", "matched", "mismatch", "unavailable"].includes(
      String(receipt.evidenceReplayStatus),
    ) ||
    !Array.isArray(receipt.wordCounts) ||
    receipt.wordCounts.length !== 3 ||
    receipt.wordCounts.some((count) => !Number.isInteger(count) || count < 45 || count > 220) ||
    !Array.isArray(receipt.requiredSections) ||
    receipt.requiredSections.join("|") !== "behavior|attack_path|engineering_response" ||
    receipt.advisoryOnly !== true ||
    typeof receipt.briefSha256 !== "string" ||
    !/^[a-f0-9]{64}$/u.test(receipt.briefSha256)
  )
    return null;
  return {
    paragraphs: paragraphs as readonly string[],
    quality: receipt as ModelExplanationView["quality"],
  };
}

/**
 * The panel. Takes `unknown` on purpose: a caller cannot hand it a pre-blessed
 * object and skip the contract, because the contract is the first thing the
 * component runs.
 */
export function ModelExplanationPanel({ explanation }: { explanation?: unknown }) {
  const parsed = parseModelExplanation(explanation);
  const headingId = useId();
  if (parsed === null) return null;
  return (
    <section className="inspector-section inspector-section--model" aria-labelledby={headingId}>
      <div className="inspector-section__heading">
        <Bot aria-hidden="true" size={16} />
        <h3 data-model-generated="true" id={headingId}>
          {MODEL_EXPLANATION_MARKER}
        </h3>
        <Badge label="Not part of the finding record" tone="warning" />
      </div>
      <div className="model-brief__hero">
        <span className="model-brief__spark" aria-hidden="true">
          <Sparkles size={18} />
        </span>
        <div>
          <strong>Evidence brief</strong>
          <p>
            Three display-only checkpoints: what broke, how it can hurt, and what must be verified
            before the risk can close.
          </p>
        </div>
      </div>
      <div className="model-explanation__grid">
        {parsed.paragraphs.map((paragraph, index) => {
          const section = BRIEF_SECTIONS[index] ?? DEFAULT_BRIEF_SECTION;
          const Icon = section.Icon;
          return (
            <article
              className={index === 0 ? "model-explanation__lead" : "model-explanation__card"}
              key={`${section.title}-${paragraph}`}
            >
              <div className="model-explanation__card-heading">
                <span className="model-explanation__card-icon" aria-hidden="true">
                  <Icon size={17} />
                </span>
                <div>
                  <span>{section.eyebrow}</span>
                  <h4>{section.title}</h4>
                </div>
              </div>
              <p className="model-explanation__signal">{paragraph}</p>
            </article>
          );
        })}
      </div>
      <details className="model-brief__quality">
        <summary>Quality receipt available · replay {parsed.quality.evidenceReplayStatus}</summary>
        <p>
          Receipt {parsed.quality.briefSha256.slice(0, 12)} ·{" "}
          {compactCitation(parsed.quality.findingCitation)} ·{" "}
          {compactCitation(parsed.quality.evidenceCitation)}. This receipt proves structural
          completeness and citations, not factual truth.
        </p>
      </details>
    </section>
  );
}
